import React from "react";
import { Box, IconButton, Textarea } from "@mui/joy";
import { theme } from "styles/theme";

export function ChatInput({
  value,
  onChange,
  onSubmit,
  onStop,
  disabled,
  isStreaming,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  disabled: boolean;
  isStreaming: boolean;
  placeholder?: string;
}) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit();
    }
  };
  return (
    <Box
      sx={{
        m: "8px 12px 12px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        p: "6px 6px 6px 12px",
        borderRadius: "16px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:focus-within": {
          borderColor: theme.vars.palette.Border.Highlight,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10)",
        },
      }}
    >
      <Textarea
        variant="plain"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        minRows={1}
        maxRows={6}
        disabled={disabled || isStreaming}
        sx={{
          flex: 1,
          fontSize: "13px",
          backgroundColor: "transparent",
          "--Textarea-focusedHighlight": "transparent",
          "&::before": { display: "none" },
          "&:hover": { backgroundColor: "transparent" },
        }}
      />
      <IconButton
        variant="solid"
        color="primary"
        onClick={isStreaming ? onStop : onSubmit}
        disabled={isStreaming ? false : disabled || !value.trim()}
        sx={{
          minHeight: "32px",
          minWidth: "32px",
          fontSize: "15px",
          borderRadius: "999px",
          // 정지 버튼은 primary 강조 대신 중립 톤 — 전송(primary)과 시각적 위계를 구분해
          // "보내기"가 아닌 "멈추기" 임을 드러내고 인풋 영역과 통일감을 준다.
          backgroundColor: isStreaming
            ? theme.vars.palette.neutral[200]
            : theme.vars.palette.Border.Highlight,
          color: isStreaming
            ? theme.vars.palette.Content.Strong
            : theme.vars.palette.common.white,
          "&:hover": {
            backgroundColor: isStreaming
              ? theme.vars.palette.neutral[300]
              : theme.vars.palette.Border.Highlight,
            opacity: isStreaming ? 1 : 0.9,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.vars.palette.primary.deactivated,
            color: theme.vars.palette.common.white,
          },
        }}
        title={isStreaming ? "정지" : "전송 (Enter)"}
      >
        {isStreaming ? (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              backgroundColor: theme.vars.palette.Border.Highlight,
            }}
          />
        ) : (
          "↑"
        )}
      </IconButton>
    </Box>
  );
}
